import { useEffect, useRef, useState } from "react";
import * as Tone from "tone";
import { ensureAudioReady, getPiano, kickAudio, onAudioStop, stopAllAudio } from "@/audio/piano";

/**
 * Visualises and plays a one-bar rhythmic pattern. Each cell is a subdivision.
 * Useful for Oom-Pah, 6/8, Charleston, Tresillo, Konnakol, etc.
 */

export interface RhythmCell {
  /** Hand label: "L" | "R" | both */
  hand?: "L" | "R" | "B";
  /** Optional pitch (single or chord). If absent and hand is set, plays a click instead. */
  pitches?: string[];
  /** "x" beat marker if true. */
  on?: boolean;
  /** Caption shown under the cell (e.g. "Doh", "Tha", "&"). */
  caption?: string;
}

export interface RhythmGridProps {
  cells: RhythmCell[];
  /** Cells per beat, used for beat lines. */
  subdivisions?: number;
  /** Beats per bar (default 4). */
  beats?: number;
  bpm?: number;
  /** Title shown above grid. */
  label?: string;
  /**
   * Time signature to display, e.g. "6/8". Defaults to `beats`/4, because the
   * beat unit is a property of the METER, not of how finely the grid is drawn
   * — deriving the denominator from `subdivisions` labelled a sixteenth-note
   * grid in 4/4 as "4/16".
   */
  timeSig?: string;
}

const HAND_COLORS: Record<string, string> = { L: "#3b82f6", R: "#f59e0b", B: "#a78bfa" };

export function RhythmGrid({
  cells,
  subdivisions = 2,
  beats = 4,
  bpm = 90,
  label,
  timeSig,
}: RhythmGridProps) {
  // A grid is whole bars of beats × subdivisions. A ragged grid loops on the
  // wrong beat (PF19 once crammed two bars into one) — report it, don't hide it.
  useEffect(() => {
    if (cells.length % (beats * subdivisions) !== 0) {
      console.error(`[RhythmGrid] "${label ?? "grid"}": ${cells.length} cells is not a whole number of ${beats}×${subdivisions} bars`);
    }
  }, [cells.length, beats, subdivisions, label]);
  const [tick, setTick] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const idsRef = useRef<number[]>([]);
  const endTimerRef = useRef<number | null>(null);

  const total = cells.length;
  const cellPct = 100 / total;

  /** Forget our own scheduling. Does not touch the transport. */
  const release = () => {
    const t = Tone.getTransport();
    idsRef.current.forEach((id) => t.clear(id));
    idsRef.current = [];
    if (endTimerRef.current != null) window.clearTimeout(endTimerRef.current);
    endTimerRef.current = null;
  };

  const stop = () => {
    const t = Tone.getTransport();
    release();
    t.stop();
    t.cancel();
    setPlaying(false);
    setTick(-1);
  };

  // If another player takes the transport, our sound is already gone — reflect
  // it rather than leaving the button on "Stop" over silence.
  useEffect(() => onAudioStop(() => {
    release();
    setPlaying(false);
    setTick(-1);
  }), []);

  const play = async () => {
    // Synchronously kick the AudioContext while we're still inside the user
    // gesture — must NOT be awaited yet, browsers attribute the gesture to the
    // call site, not after an await.
    kickAudio();

    // Announce on the shared bus so every other player resets its UI, instead
    // of silently stopping their audio and leaving their buttons lit.
    release();
    stopAllAudio();

    await ensureAudioReady();
    const piano = await getPiano();
    const t = Tone.getTransport();
    t.loop = false;
    t.bpm.value = bpm;

    // Pick a valid Tone.js note duration based on subdivision count.
    // 2-subs (eighths) → "8n", 3-subs (triplets) → "8t", 4-subs (sixteenths) → "16n"
    const stepDur =
      subdivisions === 2 ? "8n" :
      subdivisions === 3 ? "8t" :
      subdivisions === 4 ? "16n" :
      "8n";

    cells.forEach((c, i) => {
      // Position uses Tone's bars:beats:sixteenths grid (4 sixteenths per beat).
      // For subdivisions=2: each step = 2 sixteenths; for 4: each step = 1 sixteenth.
      const sixteenthsPerStep = 4 / subdivisions;
      const totalSixteenths = i * sixteenthsPerStep;
      const beat = Math.floor(totalSixteenths / 4);
      const sixteenth = totalSixteenths % 4;
      const time = `0:${beat}:${sixteenth}`;

      const id = t.schedule((when) => {
        if (c.on && c.pitches) piano.triggerAttackRelease(c.pitches, stepDur, when);
        Tone.Draw.schedule(() => setTick(i), when);
      }, time);
      idsRef.current.push(id);
    });

    // Auto-stop after the bar finishes. The handle is tracked so a quick
    // Stop → Play can't be cut short by the previous pass's timer.
    const beatsTotal = total / subdivisions;
    const totalSeconds = (beatsTotal / bpm) * 60 + 0.5;
    t.position = "0:0:0";
    t.start("+0.05");
    setPlaying(true);
    endTimerRef.current = window.setTimeout(stop, totalSeconds * 1000);
  };

  useEffect(() => () => stop(), []);

  return (
    <div className="nsm-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-nsm-ink">{label ?? "Rhythm pattern"}</div>
        <button onClick={playing ? stop : play} className={playing ? "nsm-btn-ghost" : "nsm-btn-primary"}>
          {playing ? "Stop" : "Play"}
        </button>
      </div>
      <div className="relative flex h-20 overflow-hidden rounded-md border border-nsm-border bg-nsm-surface">
        {/* beat dividers */}
        {Array.from({ length: beats - 1 }).map((_, i) => (
          <div
            key={`d${i}`}
            className="absolute top-0 h-full border-l border-nsm-border/80"
            style={{ left: `${((i + 1) / beats) * 100}%` }}
          />
        ))}
        {cells.map((c, i) => {
          const color = c.hand ? HAND_COLORS[c.hand] : "#262b34";
          const active = tick === i;
          return (
            <div
              key={i}
              className="relative flex h-full flex-col items-center justify-end border-r border-nsm-border/30 transition-colors"
              style={{ width: `${cellPct}%`, background: c.on ? `${color}33` : "transparent" }}
            >
              {c.on && (
                <div
                  className={`mb-3 h-3 w-3 rounded-full transition-transform ${
                    active ? "scale-150 ring-2 ring-amber-300" : ""
                  }`}
                  style={{ background: color }}
                />
              )}
              {c.caption && (
                <div className="absolute bottom-0.5 text-[10px] font-medium text-nsm-dim">
                  {c.caption}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-nsm-dim">
        <span>
          {timeSig ?? `${beats}/4`} · {bpm} bpm
        </span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full" style={{ background: HAND_COLORS.L }} /> Left
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full" style={{ background: HAND_COLORS.R }} /> Right
          </span>
        </span>
      </div>
    </div>
  );
}
